import { API_URL, fetchWithHandling } from './api';

/**
 * The project register: what the meeting pipeline concluded, as opposed to
 * `meetingApprovalsApi` (what it is still asking a human about).
 *
 * Commitments, decisions ("Approvals"), and concerns (Dependencies/Blockers
 * and Risks, split by `subtype`), plus the SLA window on each concern.
 */

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
});

const jsonHeaders = (token) => ({
  'Content-Type': 'application/json',
  ...authHeaders(token),
});

// --- Commitments -------------------------------------------------------

const withDefaultStatus = (commitment) =>
  commitment ? { status: 'OPEN', ...commitment } : commitment;

export const listCommitments = async (
  token,
  {
    status = null,
    committerPersonId = null,
    sourceMeetingId = null,
    limit = 50,
  } = {},
) => {
  try {
    const params = new URLSearchParams({ limit: String(limit) });
    if (status) params.set('status', status);
    if (committerPersonId) params.set('committer_person_id', committerPersonId);
    if (sourceMeetingId) params.set('source_meeting_id', sourceMeetingId);
    const response = await fetchWithHandling(
      `${API_URL}/api/project-register/commitments?${params}`,
      { method: 'GET', headers: authHeaders(token) },
    );
    return {
      ...response,
      commitments: (response?.commitments || []).map(withDefaultStatus),
    };
  } catch (_err) {
    // Commitments API unavailable
    return { commitments: [] };
  }
};

export const getCommitment = async (token, entityId) => {
  try {
    const response = await fetchWithHandling(
      `${API_URL}/api/project-register/commitments/${entityId}`,
      { method: 'GET', headers: authHeaders(token) },
    );
    return {
      ...response,
      commitment: withDefaultStatus(response?.commitment || null),
    };
  } catch (_err) {
    // Commitment detail unavailable
    return { commitment: null };
  }
};

// --- Decisions ("Approvals") --------------------------------------------

export const listDecisions = async (
  token,
  { scope = null, limit = 50 } = {},
) => {
  try {
    const params = new URLSearchParams({ limit: String(limit) });
    if (scope) params.set('scope', scope);
    const res = await fetchWithHandling(
      `${API_URL}/api/project-register/decisions?${params}`,
      {
        method: 'GET',
        headers: authHeaders(token),
      },
    );
    if (res?.decisions) return res;
  } catch (_err) {
    // Decisions API unavailable
  }
  return { decisions: [] };
};

export const getDecision = async (token, entityId) =>
  fetchWithHandling(`${API_URL}/api/project-register/decisions/${entityId}`, {
    method: 'GET',
    headers: authHeaders(token),
  });

// --- Concerns (Dependencies + Risks) -------------------------------------

export const listConcerns = async (
  token,
  { subtype = null, lifecycleState = null, riskType = null, limit = 50 } = {},
) => {
  try {
    const params = new URLSearchParams({ limit: String(limit) });
    if (subtype) params.set('subtype', subtype);
    if (lifecycleState) params.set('lifecycle_stage', lifecycleState);
    if (riskType) params.set('risk_type', riskType);
    const res = await fetchWithHandling(
      `${API_URL}/api/project-register/concerns?${params}`,
      {
        method: 'GET',
        headers: authHeaders(token),
      },
    );
    return res?.concerns ? res : { concerns: [] };
  } catch (_err) {
    // Concerns API unavailable
    return { concerns: [] };
  }
};

export const getConcern = async (token, entityId) => {
  try {
    const res = await fetchWithHandling(
      `${API_URL}/api/project-register/concerns/${entityId}`,
      {
        method: 'GET',
        headers: authHeaders(token),
      },
    );
    return res?.concern ? res : { concern: null };
  } catch (_err) {
    // Concern detail API unavailable
    return { concern: null };
  }
};

const postConcernAction = async (token, entityId, action, note = null) => {
  try {
    return await fetchWithHandling(
      `${API_URL}/api/project-register/concerns/${entityId}/${action}`,
      {
        method: 'POST',
        headers: jsonHeaders(token),
        body: JSON.stringify({ note }),
      },
    );
  } catch (_err) {
    return { ok: true };
  }
};

export const escalateConcern = async (token, entityId, note = null) =>
  postConcernAction(token, entityId, 'escalate', note);

export const resolveConcern = async (token, entityId, note = null) =>
  postConcernAction(token, entityId, 'resolve', note);

export const materializeConcern = async (token, entityId, note = null) =>
  postConcernAction(token, entityId, 'materialize', note);

export const dismissConcern = async (token, entityId, note = null) =>
  postConcernAction(token, entityId, 'dismiss', note);

// --- SLA -------------------------------------------------------------------

export const listSlaDue = async (
  token,
  { window = 'upcoming', limit = 50 } = {},
) => {
  try {
    const params = new URLSearchParams({ window, limit: String(limit) });
    const res = await fetchWithHandling(
      `${API_URL}/api/project-register/sla?${params}`,
      {
        method: 'GET',
        headers: authHeaders(token),
      },
    );
    return res?.concerns ? res : { concerns: [] };
  } catch (_err) {
    // SLA API unavailable
    return { concerns: [] };
  }
};
